import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "./ui/button";
import { Menu, X, Facebook, Instagram, Youtube } from "lucide-react";
import { trackCTAClick } from "../utils/analytics";

export const HeaderCRO = ({ onEnrollClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    // Check initial scroll position
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setIsMobileMenuOpen(false);
    }
  };

  const navItems = [
    { label: "Programmes", id: "programmes" },
    { label: "About", id: "about" },
    { label: "Alumni", id: "alumni" },
    { label: "Testimonials", id: "testimonials" },
    { label: "FAQ", id: "faq" },
  ];

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-purple-100/50 shadow-lg shadow-purple-100/20"
          : "backdrop-blur-none border-b border-transparent"
      }`}
      style={
        !isScrolled
          ? {
              backgroundColor: "transparent",
              background: "transparent",
            }
          : {}
      }
    >
      <div
        className="max-w-7xl mx-auto px-6 lg:px-8"
        style={!isScrolled ? { backgroundColor: "transparent" } : {}}
      >
        <div className="flex items-center justify-center h-20 relative">
          {/* Logo - Left */}
          <div className="absolute left-0 flex items-center">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="transition-opacity hover:opacity-80"
            >
              <img
                src="/images/logo.png"
                alt="The Free Birds Collective"
                className={`h-16 sm:h-20 w-auto object-contain ${
                  !isScrolled ? "drop-shadow-lg brightness-0 invert" : ""
                }`}
                style={
                  !isScrolled
                    ? {
                        filter:
                          "drop-shadow(0 2px 4px rgba(0,0,0,0.3)) invert(1) brightness(1.2)",
                      }
                    : {}
                }
              />
            </button>
          </div>

          {/* Desktop Navigation - Center */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`text-sm font-medium transition-colors duration-300 relative group ${
                  isScrolled
                    ? "text-gray-700 hover:text-purple-600"
                    : "text-white hover:text-purple-300 drop-shadow-lg"
                }`}
                style={
                  !isScrolled
                    ? {
                        color: "white",
                        textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                      }
                    : {}
                }
              >
                {item.label}
                <span
                  className={`absolute bottom-0 left-0 w-0 h-0.5 transition-all duration-300 group-hover:w-full ${
                    isScrolled ? "bg-purple-600" : "bg-purple-300"
                  }`}
                ></span>
              </button>
            ))}
          </nav>

          {/* Social Links & CTA Button & Mobile Menu - Right */}
          <div className="absolute right-0 flex items-center gap-3">
            {/* Social Links - Desktop */}
            <div className="hidden lg:flex items-center gap-2">
              <a
                href="https://www.facebook.com/thefreebirdscollective"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isScrolled
                    ? "text-gray-600 hover:text-purple-600 hover:bg-purple-50"
                    : "text-white hover:text-white hover:bg-white/10 drop-shadow-lg"
                }`}
                style={
                  !isScrolled
                    ? {
                        color: "white",
                        filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                      }
                    : {}
                }
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href="https://www.instagram.com/barryjohnsfreebirds/"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isScrolled
                    ? "text-gray-600 hover:text-purple-600 hover:bg-purple-50"
                    : "text-white hover:text-white hover:bg-white/10 drop-shadow-lg"
                }`}
                style={
                  !isScrolled
                    ? {
                        color: "white",
                        filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                      }
                    : {}
                }
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="https://www.youtube.com/@Thefreebirdscollective"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isScrolled
                    ? "text-gray-600 hover:text-purple-600 hover:bg-purple-50"
                    : "text-white hover:text-white hover:bg-white/10 drop-shadow-lg"
                }`}
                style={
                  !isScrolled
                    ? {
                        color: "white",
                        filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                      }
                    : {}
                }
                aria-label="YouTube"
              >
                <Youtube className="w-4 h-4" />
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden p-2 transition-colors ${
                isScrolled
                  ? "text-gray-700 hover:text-purple-600"
                  : "text-white hover:text-purple-300"
              }`}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Modal - Rendered via Portal outside header */}
      {isMounted &&
        isMobileMenuOpen &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] md:hidden animate-in fade-in duration-300"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            {/* Modal Content */}
            <div className="fixed inset-y-0 right-0 w-full max-w-sm bg-white shadow-2xl z-[9999] md:hidden animate-in slide-in-from-right duration-300">
              <div className="flex flex-col h-full">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <img
                    src="/images/logo.png"
                    alt="The Free Birds Collective"
                    className="h-12 w-auto object-contain"
                  />
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                    aria-label="Close menu"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Modal Content */}
                <nav className="flex-1 overflow-y-auto py-6">
                  <div className="flex flex-col gap-2 px-4">
                    {navItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className="text-left px-4 py-3 text-base font-medium text-gray-700 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all duration-300"
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>

                  {/* Apply Now Button */}
                  <div className="px-4 mt-6">
                    <Button
                      onClick={() => {
                        trackCTAClick(
                          "header_mobile_menu",
                          "Apply Now",
                          "header"
                        );
                        setIsMobileMenuOpen(false);
                        onEnrollClick();
                      }}
                      className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white shadow-lg transition-all duration-300 py-6 text-base font-semibold"
                    >
                      Apply Now
                    </Button>
                  </div>

                  {/* Social Links */}
                  <div className="flex items-center justify-center gap-4 px-4 mt-8 pt-6 border-t border-gray-200">
                    <a
                      href="https://www.facebook.com/thefreebirdscollective"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-full bg-gray-100 hover:bg-purple-100 text-gray-600 hover:text-purple-600 flex items-center justify-center transition-all duration-300"
                      aria-label="Facebook"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                    <a
                      href="https://www.instagram.com/barryjohnsfreebirds/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-full bg-gray-100 hover:bg-purple-100 text-gray-600 hover:text-purple-600 flex items-center justify-center transition-all duration-300"
                      aria-label="Instagram"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                    <a
                      href="https://www.youtube.com/@Thefreebirdscollective"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-full bg-gray-100 hover:bg-purple-100 text-gray-600 hover:text-purple-600 flex items-center justify-center transition-all duration-300"
                      aria-label="YouTube"
                    >
                      <Youtube className="w-5 h-5" />
                    </a>
                  </div>
                </nav>
              </div>
            </div>
          </>,
          document.body
        )}
    </header>
  );
};
